/**
 * QtyOverrideGrid — "Paste from BOQ" modal. A textarea of `taskKey  qty` lines
 * parsed against the preview's known leaf keys via the pure `parseBomPaste`
 * helper; valid rows are merged into the wizard's qtyOverrides map (→ re-preview),
 * unknown/non-positive rows are reported inline and nothing is partial-applied.
 * Visual register: Sovereign Emerald.
 */
import { Alert, Input, Modal } from 'antd';
import { useEffect, useState } from 'react';
import { SOVEREIGN } from '../../../utils/epcProjectVisual';
import { parseBomPaste } from '../applyBom';

export interface QtyOverrideGridProps {
    open: boolean;
    /** Leaf task keys accepted by the paste (unknown keys are rejected). */
    leafKeys: string[];
    onClose: () => void;
    /** Called with the valid overrides only when the paste has no errors. */
    onApply: (overrides: { taskKey: string; qty: number }[]) => void;
}

export function QtyOverrideGrid({ open, leafKeys, onClose, onApply }: QtyOverrideGridProps) {
    const [text, setText] = useState('');
    const [errors, setErrors] = useState<string[]>([]);

    useEffect(() => {
        if (!open) {
            setText('');
            setErrors([]);
        }
    }, [open]);

    function handleOk() {
        const { overrides, errors: parseErrors } = parseBomPaste(text, new Set(leafKeys));
        if (parseErrors.length > 0) {
            setErrors(parseErrors);
            return;
        }
        onApply(overrides);
        onClose();
    }

    return (
        <Modal
            open={open}
            onCancel={onClose}
            onOk={handleOk}
            okText="Apply overrides"
            width={560}
            destroyOnHidden
            maskClosable={false}
            title={
                <span className="text-[14px] font-bold" style={{ color: SOVEREIGN.title }}>
                    Paste quantities from BOQ
                </span>
            }
            styles={{
                header: {
                    background: SOVEREIGN.canvas,
                    borderBottom: `1px solid ${SOVEREIGN.cardBorder}`,
                    paddingBottom: 12,
                },
                body: { background: SOVEREIGN.paper, padding: 20 },
                footer: {
                    background: SOVEREIGN.canvas,
                    borderTop: `1px solid ${SOVEREIGN.cardBorder}`,
                    paddingTop: 12,
                },
            }}
            okButtonProps={{
                style: { background: SOVEREIGN.btn, borderColor: SOVEREIGN.btnBorder },
            }}
        >
            <p className="mb-2 text-[12px] font-semibold" style={{ color: SOVEREIGN.kpiCaption }}>
                One task per line: the task key, then the quantity, separated by a tab, space or
                comma — e.g. <span className="font-mono">civil.piling&nbsp;&nbsp;120</span>.
            </p>
            <Input.TextArea
                value={text}
                onChange={(e) => {
                    setText(e.target.value);
                    if (errors.length > 0) setErrors([]);
                }}
                rows={8}
                autoFocus
                placeholder={'civil.piling\t120\nelec.dc.string  340'}
                style={{
                    fontFamily: 'ui-monospace, monospace',
                    fontSize: 12,
                    background: SOVEREIGN.inputBg,
                    borderColor: SOVEREIGN.inputBorder,
                }}
            />
            {errors.length > 0 && (
                <Alert
                    className="mt-3"
                    type="error"
                    showIcon
                    message={`${errors.length} ${errors.length === 1 ? 'line' : 'lines'} could not be parsed — nothing was applied`}
                    description={
                        <ul className="m-0 max-h-40 list-disc overflow-y-auto pl-5">
                            {errors.map((e) => (
                                <li key={e} className="text-[12px]">
                                    {e}
                                </li>
                            ))}
                        </ul>
                    }
                />
            )}
        </Modal>
    );
}

export default QtyOverrideGrid;
